// Pinned-table clarification helpers (roadmap #184). PURE — no React, no network.
//
// REAL BUG, reported on a customer's WoW Media template (Dev, pivot B8:L57): the Tables step
// listed "10 things need a quick explanation" and every item was labelled only "This cell" —
// nothing named a position. The backend now sends grouped, cell-specific `issues` (address,
// row label, column header, value, plain-language reason) plus non-blocking `notes`; these
// helpers prefer that payload and, for an older payload/state without it, rebuild an
// equivalent from `table.cells` so a label can NEVER be empty or generic.

function cellAddress(c) {
  return c.address || `${c.col}${c.row}`;
}

function sheetAddress(c, sheet) {
  if (c.sheet_address) return c.sheet_address;
  return sheet ? `${sheet}!${cellAddress(c)}` : cellAddress(c);
}

function legacyKey(c) {
  const label = (c.raw_label || "").trim();
  if (label) return `UNKNOWN_LABEL|label:${label.toLowerCase()}`;
  const m = (c.reason || "").match(/^'([^']+)'/);
  if (m) return `UNMAPPED_COLUMN|col:${m[1].toLowerCase()}`;
  return `UNKNOWN|reason:${(c.reason || "").trim().toLowerCase()}|row:${c.row}`; // same cause + same row = one issue
}

function legacyHeadline(code, cells, sheet, c0) {
  const subject = cells.length === 1 ? `Cell ${sheetAddress(c0, sheet)}` : `${cells.length} cells`;
  const label = (c0.raw_label || "").trim();
  if (code === "UNKNOWN_LABEL") return `${subject} ${cells.length === 1 ? "shows" : "show"} "${label}", which we don't know yet`;
  if (code === "UNMAPPED_COLUMN") {
    const col = (c0.reason || "").match(/^'([^']+)'/)[1];
    return `${subject} ${cells.length === 1 ? "uses" : "use"} the column "${col}", which isn't in this project's data`;
  }
  return `${subject} couldn't be matched to your data`;
}

// The grouped issues for one table — the backend's `issues` when present, else rebuilt from
// its unresolved cells with the same grouping rule (same cause = one issue).
export function issuesForTable(table) {
  if (table && Array.isArray(table.issues)) return table.issues;
  const sheet = table && table.sheet;
  const groups = [];
  const byKey = new Map();
  for (const c of (table && table.cells) || []) {
    if (c.kind !== "unresolved") continue;
    const key = c.issue_key || legacyKey(c);
    if (!byKey.has(key)) {
      const g = { issue_key: key, reason_code: c.reason_code || key.split("|")[0], cells: [], _first: c };
      byKey.set(key, g);
      groups.push(g);
    }
    byKey.get(key).cells.push({ ...c, address: cellAddress(c), sheet_address: sheetAddress(c, sheet) });
  }
  return groups.map(({ _first, ...g }) => {
    const label = (_first.raw_label || "").trim();
    return {
      ...g,
      headline: legacyHeadline(g.reason_code, g.cells, sheet, _first),
      reason_text: _first.reason_text || _first.reason || "",
      action_text: label ? `Explain what "${label}" means in your report.` : "Re-add this table to see what needs attention.",
      explainable: Boolean(label),
      explain_label: label || null,
      explain_placeholder: label ? `What does "${label}" mean in your report? e.g. "sum of amount spent divided by clicks"` : null,
    };
  });
}

// Non-blocking data notes (e.g. Excel's "(blank)" pivot row) — never hidden.
export function notesForTable(table) {
  if (table && Array.isArray(table.notes)) return table.notes;
  const byRow = new Map();
  for (const c of (table && table.cells) || []) {
    if (!c.pivot_blank_row) continue;
    if (!byRow.has(c.row)) byRow.set(c.row, []);
    byRow.get(c.row).push(c);
  }
  const sheet = table && table.sheet;
  return [...byRow.entries()].map(([row, cells]) => {
    const rng = cells.length === 1 ? `${cells[0].col}${row}` : `${cells[0].col}${row}:${cells[cells.length - 1].col}${row}`;
    const where = sheet ? `${sheet}!${rng}` : rng;
    const field = cells[0].row_field || "value for this row";
    return {
      severity: "info", code: "PIVOT_BLANK_ROW", row, range: rng, sheet_address: where,
      text: `Row "(blank)" (${where}): Excel's pivot adds this row for source rows with no ${field} (often empty rows at the end of the data range). It has no values, so it's shown empty here and not counted under any ${field}.`,
    };
  });
}

// One cell, in words: `Sheet!C42 · row "(blank)" × "Sum of Impressions" · shows "1,234"`.
export function issueCellLine(cell) {
  const parts = [cell.sheet_address || cellAddress(cell)];
  if (cell.row_label && cell.column_header) parts.push(`row "${cell.row_label}" × "${cell.column_header}"`);
  else if (cell.row_label) parts.push(`row "${cell.row_label}"`);
  else if (cell.column_header) parts.push(`column "${cell.column_header}"`);
  if (cell.display_value !== undefined && cell.display_value !== null && cell.display_value !== "") parts.push(`shows "${cell.display_value}"`);
  if (cell.formula) parts.push(`formula =${cell.formula}`);
  return parts.join(" · ");
}

// One sentence for the table's status line.
export function tableSummary(table) {
  if (table && table.summary) return table.summary;
  const issues = issuesForTable(table);
  if (!issues.length) return null;
  if (issues.length === 1) return `${issues[0].headline}.`;
  return `${issues.length} issues stop this table from publishing: ${issues[0].headline}, and ${issues.length - 1} more.`;
}
